/** @jsxImportSource @emotion/react */
import { DesignSystemProvider } from '@quickbit/qb-design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Fragment, lazy, Suspense } from 'react';
import { IntlProvider } from 'react-intl';
// eslint-disable-next-line no-restricted-imports
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { useAsync } from 'react-use';
import { getMicroArticles } from 'api/contentful/getMicroArticles';
import {
  ProtectedRoute,
  NavbarLayout,
  QBLoader,
  LayoutRoute,
} from 'components';
import { WalletsProvider } from 'context';
import { useLocale, useIosTrackingPermission } from 'hooks';
import {
  OnrampSendPreview,
  OnrampSendProcessing,
  OnrampBuy,
  OnrampOutlet,
  OnrampOnboardingLayout,
  OnrampOnboardingSteps,
  OnrampLogin,
  OnrampFailure,
  OnrampEmailLogin,
  OnrampSuccess,
  OnrampCashierBuy,
  OnrampCompleted,
  OnrampCancelled,
} from 'onramp-pages'; // Onramp can't be lazy imported, will messes up the mixpanel tracking
import { AuthenticatorRecovery } from 'pages/AuthenticatorRecovery';
import { DeleteAccount } from 'pages/Profile/pages';
import { Authenticator } from 'pages/Profile/pages/Authenticator';
import { TravelRuleOriginatorInfo } from 'pages/TravelRuleBeneficiaryInfo/TravelRuleOriginatorInfo';
import { renderText, getMessages } from 'utils';
import { environment } from 'utils/environmentVariables';
import { lazily } from 'utils/lazily';

const HomeRouter = lazy(() => import('pages/Home'));

const {
  OnboardingLayout,
  Kyc,
  EmailVerification,
  PhoneVerification,
  ContactInformation,
  SourceOfFunds,
  Steps,
  Consent,
} = lazily(() => import('pages/Onboarding'));

const AccountOnHold = lazy(() => import('pages/AccountOnHold'));
const AccountBlocked = lazy(() => import('pages/AccountBlocked'));
const AccountClosed = lazy(() => import('pages/AccountClosed'));

const SetPartialOrderAmonut = lazy(() => import('pages/SetPartialOrderAmount'));

const AppDownload = lazy(() => import('pages/AppDownload'));

const Support = lazy(() => import('pages/Support'));

const SetPassword = lazy(() => import('pages/Onboarding/SetPassword'));

const TravelRuleBeneficiaryInfoForSending = lazy(
  () =>
    import(
      'pages/TravelRuleBeneficiaryInfo/TravelRuleBeneficiaryInfoForSending'
    )
);
const TravelRuleBeneficiaryInfoForContact = lazy(
  () =>
    import(
      'pages/TravelRuleBeneficiaryInfo/TravelRuleBeneficiaryInfoForContact'
    )
);
const PaymentIq = lazy(() => import('pages/PaymentIq'));

const { ForgotPassword, Login, EmailLogin } = lazily(
  () => import('pages/Login')
);

const NotFound = lazy(() => import('pages/NotFound'));
const FeatureFlags = lazy(() => import('pages/FeatureFlags'));

const WalletRouter = lazy(() => import('pages/Wallet'));

const {
  Profile,
  AccountInformation,
  TermsAndPolicies,
  TransactionHistoryReport,
  GDPR,
  Consent: ProfileConsent,
  ChangePassword,
  ProfileOutlet,
} = lazily(() => import('pages/Profile'));

const { PasswordReset } = lazily(() => import('pages/PasswordReset'));

const queryClient = new QueryClient();

const App = () => {
  const { locale } = useLocale();

  // Initialize iOS tracking permission check on app startup
  useIosTrackingPermission();

  const { value: content, loading } = useAsync(async () => {
    const microArticles = await getMicroArticles('en-US');
    return { ...getMessages(locale), ...microArticles };
  }, [locale]);

  if (loading) return null;

  return (
    <IntlProvider
      locale={locale}
      messages={content}
      textComponent={({ children }: { children?: string[] }) => {
        if (!children) return null;
        return (
          <Fragment>
            {renderText(Array.isArray(children) ? children[0] : children)}
          </Fragment>
        );
      }}
    >
      <DesignSystemProvider>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<QBLoader />}>
            <Routes>
              {/* public APP routes */}
              <Route
                path="/login"
                element={
                  <LayoutRoute.Desktop>
                    <Login />
                  </LayoutRoute.Desktop>
                }
              >
                <Route
                  index
                  element={
                    <LayoutRoute.Mobile>
                      <Login />
                    </LayoutRoute.Mobile>
                  }
                />
                <Route
                  path="forgot-password"
                  element={
                    <LayoutRoute.DesktopModal onCloseNavigate={-1}>
                      <ForgotPassword />
                    </LayoutRoute.DesktopModal>
                  }
                />
                <Route
                  path="email"
                  element={
                    <LayoutRoute.DesktopModal onCloseNavigate={-1}>
                      <EmailLogin />
                    </LayoutRoute.DesktopModal>
                  }
                />
              </Route>
              <Route path="/account-on-hold" element={<AccountOnHold />} />
              <Route path="/account-blocked" element={<AccountBlocked />} />
              <Route path="/account-closed" element={<AccountClosed />} />
              <Route path="/support" element={<Support />} />
              <Route path="/app-download" element={<AppDownload />} />
              <Route path="/password-reset" element={<PasswordReset />} />
              <Route
                path="/authenticator-recovery"
                element={<AuthenticatorRecovery />}
              />
              <Route path="/feature-flags" element={<FeatureFlags />} />
              <Route
                path="/contact-travel-rule-beneficiary-info/:contactId"
                element={<TravelRuleBeneficiaryInfoForContact />}
              />
              <Route
                path="/travel-rule-beneficiary-info/:sendOrderPreviewId"
                element={<TravelRuleBeneficiaryInfoForSending />}
              />
              <Route
                path="/travel-rule-originator-info/:transactionId"
                element={<TravelRuleOriginatorInfo />}
              />
              <Route path="/payment-iq/:buyPreviewId" element={<PaymentIq />} />
              <Route
                path="/partial-orders/set-amount"
                element={<SetPartialOrderAmonut />}
              />

              {/* protected APP routes */}
              <Route element={<ProtectedRoute redirectUrl="/login" />}>
                <Route path="onboarding" element={<OnboardingLayout />}>
                  <Route index element={<Navigate to="steps" replace />} />
                  <Route path="steps" element={<Steps />} />
                  <Route path="set-password" element={<SetPassword />} />
                  <Route path="consent" element={<Consent />} />
                  {!environment.READONLY && (
                    <>
                      <Route
                        path="email-verification"
                        element={<EmailVerification />}
                      />
                      <Route
                        path="phone-verification"
                        element={<PhoneVerification />}
                      />
                      <Route
                        path="contact-information"
                        element={<ContactInformation />}
                      />
                      <Route
                        path="source-of-funds"
                        element={<SourceOfFunds />}
                      />
                      <Route path="kyc" element={<Kyc />}>
                        <Route path=":questionCode" element={<Kyc />} />
                      </Route>
                    </>
                  )}
                </Route>
                <Route path="/not-found" element={<NotFound />} />
                <Route element={<AppLayout />}>
                  <Route path="/" element={<Navigate to="home" />} />
                  <Route path="/home/*" element={<HomeRouter />} />
                  <Route path=":currencyCode/*" element={<WalletRouter />} />
                  <Route path="profile" element={<ProfileOutlet />}>
                    <Route index element={<Profile />} />
                    <Route
                      path="account-information"
                      element={
                        <LayoutRoute.Desktop>
                          <AccountInformation />
                        </LayoutRoute.Desktop>
                      }
                    >
                      <Route
                        index
                        element={
                          <LayoutRoute.Mobile>
                            <AccountInformation />
                          </LayoutRoute.Mobile>
                        }
                      />
                      <Route
                        path="delete-account"
                        element={
                          <LayoutRoute.DesktopModal onCloseNavigate={-1}>
                            <DeleteAccount />
                          </LayoutRoute.DesktopModal>
                        }
                      />
                    </Route>
                    <Route path="authenticator" element={<Authenticator />} />
                    <Route
                      path="change-password"
                      element={<ChangePassword />}
                    />
                    <Route
                      path="terms-and-policies"
                      element={<TermsAndPolicies />}
                    />
                    <Route path="gdpr" element={<GDPR />} />
                    <Route path="consent" element={<ProfileConsent />} />
                    <Route
                      path="transaction-report"
                      element={<TransactionHistoryReport />}
                    />
                  </Route>
                </Route>
              </Route>

              <Route path="onramp" element={<OnrampOutlet />}>
                {/* public Onramp routes */}
                <Route
                  path="login"
                  element={
                    <LayoutRoute.Desktop>
                      <OnrampLogin />
                    </LayoutRoute.Desktop>
                  }
                >
                  <Route
                    index
                    element={
                      <LayoutRoute.Mobile>
                        <OnrampLogin />
                      </LayoutRoute.Mobile>
                    }
                  />
                  <Route
                    path="forgot-password"
                    element={
                      <LayoutRoute.DesktopModal onCloseNavigate={-1}>
                        <ForgotPassword />
                      </LayoutRoute.DesktopModal>
                    }
                  />
                  <Route
                    path="email"
                    element={
                      <LayoutRoute.DesktopModal onCloseNavigate={-1}>
                        <OnrampEmailLogin />
                      </LayoutRoute.DesktopModal>
                    }
                  />
                </Route>
                <Route path="account-on-hold" element={<AccountOnHold />} />
                <Route path="account-blocked" element={<AccountBlocked />} />
                <Route path="account-closed" element={<AccountClosed />} />
                {/* protected TopUp routes */}
                <Route element={<ProtectedRoute redirectUrl="/onramp/login" />}>
                  <Route path="failure" element={<OnrampFailure />} />
                  <Route path="onboarding" element={<OnrampOnboardingLayout />}>
                    <Route index element={<Navigate to="steps" />} />
                    <Route path="set-password" element={<SetPassword />} />
                    <Route path="consent" element={<Consent />} />
                    <Route path="steps" element={<OnrampOnboardingSteps />} />
                    <Route
                      path="email-verification"
                      element={<EmailVerification />}
                    />
                    <Route
                      path="phone-verification"
                      element={<PhoneVerification />}
                    />
                    <Route
                      path="contact-information"
                      element={<ContactInformation />}
                    />
                    <Route path="kyc" element={<Kyc />}>
                      <Route path=":questionCode" element={<Kyc />} />
                    </Route>
                    <Route path="source-of-funds" element={<SourceOfFunds />} />
                  </Route>
                  <Route index element={<OnrampSendPreview />} />
                  <Route path="processing" element={<OnrampSendProcessing />} />
                  <Route path="buy">
                    <Route index element={<OnrampBuy />} />
                    <Route path="cashier" element={<OnrampCashierBuy />} />
                  </Route>
                  <Route path="success" element={<OnrampSuccess />} />
                  <Route path="completed" element={<OnrampCompleted />} />
                  <Route path="cancelled" element={<OnrampCancelled />} />
                </Route>
              </Route>
              {/* catch all non existing routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ReactQueryDevtools />
        </QueryClientProvider>
      </DesignSystemProvider>
    </IntlProvider>
  );
};

const AppLayout = () => {
  return (
    <WalletsProvider>
      <NavbarLayout>
        <Suspense fallback={<QBLoader position="left" />}>
          <Outlet />
        </Suspense>
      </NavbarLayout>
    </WalletsProvider>
  );
};

export default App;
